import { queryClient } from '@/common/lib/queryClient';
import { DB_TYPE, VIEW_MODE } from '@/common/define/common.define';
import {
  getGetV7DatasStatCommonCategoriesQueryOptions,
  getGetV7DatasStatQueryOptions,
} from '@/api-client/metav6/queries/data-stat-information-controller/data-stat-information-controller';
import { getGetV8DatabaseInstancesEnabledQueryOptions } from '@/api-client/database/queries/database-instance-v8-controller/database-instance-v8-controller';
import { getGetV8TargetIconInitQueryOptions } from '@/api-client/icon/queries/target-icon-controller/target-icon-controller';
import { getGetV8DashboardsWidgetsCustomImagesQueryOptions } from '@/api-client/metav8Over/queries/widget-custom-image-controller/widget-custom-image-controller';
import { getGetV7KubernetesClustersOpenshiftExistsQueryOptions } from '@/api-client/kubernetes/queries/kubernetes-cluster-v7-controller/kubernetes-cluster-v7-controller';
import { getGetV7PropertyQueryOptions } from '@/api-client/common/queries/mode-controller/mode-controller';
import { getV8UserEnvs } from '@/api-client/meta/queries/xm-user-env-controller/xm-user-env-controller';
import { useViewModeStore } from '@/common/stores/view-mode';
import { useUserEnvStore } from '@/common/stores/user-env';
import { useMyInfoStore } from '@/config/stores/my-info';
import { useDashboardUserEnvStore } from '@/dashboard/stores/dashboard-user-env';
import { getV11DashboardsEnv } from '@/api-client/dashboard/queries/dashboard-env-controller/dashboard-env-controller';
import { RouteLocationNormalized } from 'vue-router';

const MAXGAUGE_DB_TYPES = Object.values(DB_TYPE);

const statCategoriesByViewMode = {
  [VIEW_MODE.MAXGAUGE]: [
    'database',
    ...MAXGAUGE_DB_TYPES,
    'host',
    'custom',
    'target',
    'filesystem',
    'container',
  ],
  [VIEW_MODE.EXEMONE]: [],
};

export const prefetchStatInfo = async () => {
  const { userId } = useMyInfoStore().myInfo;
  const { viewMode } = useViewModeStore();

  await Promise.all([
    queryClient.prefetchQuery({
      ...getGetV7DatasStatCommonCategoriesQueryOptions({
        categories: statCategoriesByViewMode[viewMode],
      }),
      staleTime: Infinity,
      gcTime: Infinity,
    }),
    queryClient.prefetchQuery({
      ...getGetV7DatasStatQueryOptions({
        userId: userId ?? -1,
      }),
      staleTime: Infinity,
      gcTime: Infinity,
    }),
  ]);
};

export const prefetchStatData = async () => {
  const { userId } = useMyInfoStore().myInfo;

  await queryClient.prefetchQuery({
    ...getGetV7DatasStatQueryOptions({
      userId: userId ?? -1,
    }),
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

/**
 * maxgauge instance count prefetch
 * Oracle, MySQL, PostgreSQL, SQL Server GNB에서 사용
 */
export const prefetchInstanceCount = async () => {
  const { viewMode } = useViewModeStore();

  if (viewMode !== VIEW_MODE.MAXGAUGE) {
    return;
  }

  await queryClient.prefetchQuery({
    ...getGetV8DatabaseInstancesEnabledQueryOptions({}),
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

/**
 * common images prefetch
 * Target Icon, Dashboard Widget Custom Images
 */
export const prefetchCommonImages = async () => {
  await Promise.all([
    queryClient.prefetchQuery({
      ...getGetV8TargetIconInitQueryOptions(),
      staleTime: Infinity,
      gcTime: Infinity,
    }),
    queryClient.prefetchQuery({
      ...getGetV8DashboardsWidgetsCustomImagesQueryOptions(),
      staleTime: Infinity,
      gcTime: Infinity,
    }),
  ]);
};

/**
 * custom env prefetch
 * OpenShift 클러스터 존재 여부 등
 */
export const prefetchCustomEnv = async () => {
  await queryClient.prefetchQuery({
    ...getGetV7KubernetesClustersOpenshiftExistsQueryOptions(),
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

/**
 * property prefetch
 * 서버 설정 정보
 */
export const prefetchProperty = async () => {
  await queryClient.prefetchQuery({
    ...getGetV7PropertyQueryOptions(),
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

/**
 * user env prefetch
 * 사용자 환경 설정 정보 (global + custom)
 */
export const prefetchUserEnv = async (options?: { refresh?: boolean }) => {
  const refresh = options?.refresh ?? true;
  const userEnvStore = useUserEnvStore();

  const [customEnvResult, globalEnvResult] = await Promise.all([
    getV8UserEnvs({ global: false }),
    getV8UserEnvs({ global: true }),
  ]);

  const allEnvData = [
    ...(customEnvResult?.data?.data ?? []),
    ...(globalEnvResult?.data?.data ?? []),
  ];

  if (refresh) {
    userEnvStore.fetchAllEnvData(allEnvData);
  } else {
    userEnvStore.addNewEnvData(allEnvData);
  }
};

/**
 * dashboarduser env prefetch
 * 사용자 환경 설정 정보 (global + custom)
 *
 * `setId: false` 는 읽기 전용 소비자용 — dashboardId 를 심지 않아 store 의 env 쓰기가 no-op 으로 남는다.
 * `timeout` 은 이 조회가 렌더의 차단 의존성인 호출부에서만 지정한다 (미지정 시 기존대로 상한 없음).
 */
export const prefetchDashboardUserEnv = async (
  dashboardId: number,
  options?: { refresh?: boolean; setId?: boolean; timeout?: number },
) => {
  if (isNaN(dashboardId)) {
    return;
  }

  const refresh = options?.refresh ?? true;
  const setId = options?.setId ?? true;
  const requestOptions = { timeout: options?.timeout };
  const dashboardUserEnvStore = useDashboardUserEnvStore();

  const [customEnvResult, globalEnvResult] = await Promise.allSettled([
    getV11DashboardsEnv({ dashboardId, global: false }, requestOptions),
    getV11DashboardsEnv({ dashboardId, global: true }, requestOptions),
  ]);

  const allEnvData = [customEnvResult, globalEnvResult].flatMap((result) => {
    if (result.status === 'rejected') {
      console.error('dashboard user env 조회 실패:', dashboardId, result.reason);
      return [];
    }

    return result.value.data?.data ?? [];
  });

  if (setId) {
    dashboardUserEnvStore.setDashboardId(dashboardId);
  }

  if (refresh) {
    dashboardUserEnvStore.fetchAllEnvData(allEnvData);
  } else {
    dashboardUserEnvStore.addNewEnvData(allEnvData);
  }
};

/**
 * global dashboard user env만 prefetch
 */
const prefetchGlobalDashboardUserEnv = async (dashboardId: number) => {
  const dashboardUserEnvStore = useDashboardUserEnvStore();

  const { data } = await getV11DashboardsEnv({ dashboardId, global: true });
  const envData = data?.data ?? [];

  dashboardUserEnvStore.setDashboardId(dashboardId);
  dashboardUserEnvStore.addNewEnvData(envData);
};

/**
 * custom dashboard user env만 prefetch
 */
const prefetchCustomDashboardUserEnv = async (dashboardId: number) => {
  const dashboardUserEnvStore = useDashboardUserEnvStore();

  const { data } = await getV11DashboardsEnv({ dashboardId, global: false });
  const envData = data?.data ?? [];

  dashboardUserEnvStore.setDashboardId(dashboardId);
  dashboardUserEnvStore.addNewEnvData(envData);
};

/**
 * 라우트 메타·params 기준으로 dashboard user env 일부만 prefetch (대시보드 view/editor 등).
 */
export const prefetchPartialDashboardUserEnv = async (to: RouteLocationNormalized) => {
  const dashboardId = to.params?.id != null ? Number(to.params.id) : null;
  const needCustom = Boolean(to.meta?.needCustomEnvPreFetch);
  const needGlobal = Boolean(to.meta?.needGlobalEnvPreFetch);

  if (dashboardId == null || isNaN(dashboardId)) {
    return;
  }

  const promises: Promise<void>[] = [];
  if (needCustom) {
    promises.push(prefetchCustomDashboardUserEnv(dashboardId));
  }
  if (needGlobal) {
    promises.push(prefetchGlobalDashboardUserEnv(dashboardId));
  }

  await Promise.allSettled(promises);
};

/**
 * 로그아웃 시 prefetch된 캐시 제거
 */
export const clearPrefetchedCache = () => {
  queryClient.clear();
};
